import classNames from 'classnames'
import { useApp } from '../../../hooks/useApp'
import { getCorrespondingPendantProductVariantIdsByMainProductVariantId, getOptionalSinglePendantMainProductVariant } from '../../../utils'
import { useEffect, useRef } from 'react'
import { Text } from '../../ui/Text/Text'

interface Props {
  className?: string
  showLabel?: boolean
}

export const ChainVariants = ({
  className,
  showLabel,
}: Props) => {
  const {
    mainProduct,
    linkedMainProducts,
    activeMainProductVariantId,
    activePendantProductVariantIds,
    setActiveMainProductVariantId,
    setConfirmationPopupDisplayed,
    redirectingToNextPage,
    setRedirectingToNextPage,
    setBufferedMainProduct,
    setBufferedMainProductVariant,
    isMainProductSinglePendant
  } = useApp()
  const activeItemRef = useRef<HTMLLIElement>(null)

  const mainProducts = [mainProduct, ...linkedMainProducts]

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [])

  const lengthOptionWithValues = mainProduct.optionsWithValues.find(option => option.name.toLowerCase() === 'length')
  const lengthUnit = mainProduct.metafields.type === 'Bracelet' ? 'cm' : '"'
  const chainVariantsByMetal = lengthOptionWithValues?.values.map(lengthValue => mainProduct.variants.find(v => v.options[lengthOptionWithValues.position - 1] === lengthValue))
  const selectableChainVariantsByMetal = isMainProductSinglePendant
    ? chainVariantsByMetal?.filter(Boolean)
    : chainVariantsByMetal?.filter(chainVariant => chainVariant?.available)
  const shouldShowLabel = showLabel ?? true

  if (!selectableChainVariantsByMetal || selectableChainVariantsByMetal.length === 0) {
    return <p>No available chain variants.</p>
  }

  const wrapperClassName = classNames('colors-swatch-wrapper', className)
  const listClassName = classNames('colors-swatch__list', redirectingToNextPage && 'cursor-wait')

  return (
    <div className={wrapperClassName}>
      {shouldShowLabel && <span className="colors-swatch__label">Size: </span>}
      <ul className={listClassName}>
        {selectableChainVariantsByMetal!.map((productVariant) => {
          if (!productVariant) return null
          const { id, title } = productVariant
          const isSeparateProduct = !mainProduct.variants.some((mainProductVariant) => mainProductVariant.id === id)

          let buttonTitle: string = title
          const matchedMainProduct = mainProducts.find((mainProduct) => mainProduct.variants.some((variant) => variant.id === id))
          if (matchedMainProduct) {
            buttonTitle = matchedMainProduct.title + ' - ' + title
          }

          if (!productVariant.available) {
            buttonTitle += ' is out of stock'
          }

          const handleClick = () => {
            if (redirectingToNextPage) return

            if (!isSeparateProduct) {
              setActiveMainProductVariantId(id)
              return
            }

            const linkedBuilderProduct = linkedMainProducts.find((linkedBuilderProduct) => linkedBuilderProduct.variants.some((variant) => variant.id === id))

            if (!linkedBuilderProduct) return

            setRedirectingToNextPage(true)

            const allPendantsAvailable = linkedBuilderProduct.metafields.pendantProducts
              .map((pendantProduct) => pendantProduct.id)
              .every((pendantProductId) => mainProduct.metafields.pendantProducts.map((pendantProduct) => pendantProduct.id).includes(pendantProductId))

            if (allPendantsAvailable) {
              const correspondingPendantProductVariantIds = getCorrespondingPendantProductVariantIdsByMainProductVariantId(
                linkedBuilderProduct,
                id,
                activePendantProductVariantIds
              )
              const bset = [productVariant.id, ...correspondingPendantProductVariantIds].join('-')

              const url = new URL(window.location.href)
              url.pathname = linkedBuilderProduct.url
              url.searchParams.set('bset', bset)
              window.history.replaceState({}, '', url.href)

              window.reloadBuilder?.(linkedBuilderProduct)
              return
            }

            setBufferedMainProduct(linkedBuilderProduct)
            setBufferedMainProductVariant(productVariant)
            setConfirmationPopupDisplayed(true)
          }

          // @TODO: Fix types for ids
          const isActive = +id === +activeMainProductVariantId
          const purchasableVariant = isMainProductSinglePendant
            ? getOptionalSinglePendantMainProductVariant(mainProduct, +id, +activePendantProductVariantIds[0])
            : productVariant
          const isOutOfStock = !purchasableVariant?.available

          return (
            <li key={id} className={classNames('colors-swatch__item', isActive && 'colors-swatch__item--active', isOutOfStock && 'opacity-30')} ref={isActive ? activeItemRef : null}>
              <button onClick={handleClick} aria-label={buttonTitle} title={buttonTitle}>
                <Text>{productVariant.options[lengthOptionWithValues!.position - 1]}{lengthUnit}</Text>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
